import { isNullOrUndefined } from '@shared';

export function policyExtractorAggregateResults(
  parsedResponses: ParsedPolicyDataExtractResponse[],
) {
  // If there is only one response, return it
  if (parsedResponses.length === 1) {
    return parsedResponses[0];
  }

  const fieldCountMap: {
    [key in PolicyFieldId]?: { [value: string]: number };
  } = {};

  parsedResponses.forEach(response => {
    Object.entries(response).forEach(([fieldId, field]) => {
      const id = fieldId as PolicyFieldId;
      const normalizedValue = normalizeParsedFieldValue(
        field?.parsedFieldValue,
      );
      if (!fieldCountMap[id]) {
        fieldCountMap[id] = {};
      }
      fieldCountMap[id]![normalizedValue] =
        (fieldCountMap[id]![normalizedValue] || 0) + 1;
    });
  });

  // Construct a response that contains the mode value of each field
  const modeResponse: ParsedPolicyDataExtractResponse = {};
  for (const [fieldId, valueCountMap] of Object.entries(fieldCountMap)) {
    const id = fieldId as PolicyFieldId;

    // Calculate the mode value
    const modeEntry = Object.entries(valueCountMap!).reduce((a, b) =>
      b[1] > a[1] ? b : a,
    );
    // value gets turned into a string during the reduce, so we need to convert it back to the original type
    const modeValue = parseFloat(modeEntry[0])
      ? parseFloat(modeEntry[0])
      : modeEntry[0];
    const modeCount = modeEntry[1];

    // Calculate the total count of all values for this field
    const totalCount = Object.values(valueCountMap!).reduce(
      (sum, count) => sum + count,
      0,
    );

    // Calculate the prevalence ratio of the mode value
    const modePrevalenceRatio = modeCount / totalCount;

    // Find the index of a response that has the mode value for this field
    const matchingFieldIndex = parsedResponses.findIndex(
      response =>
        normalizeParsedFieldValue(response[id]?.parsedFieldValue) === modeValue,
    );

    // If a matching field is found, add it to the aggregated response
    if (matchingFieldIndex !== -1) {
      const matchingField = parsedResponses[matchingFieldIndex];
      // not type safe, to do later
      (modeResponse as any)[id] = {
        ...matchingField[id],
        modePrevalenceRatio, // Adding the modePrevalenceRatio to the response
      };
    } else {
      console.log(
        `field ${id} was ommitted from the modeResponse. modeValue: ${modeValue}`,
      );
    }
  }

  if (Object.keys(modeResponse).length === 0) {
    throw new Error(
      'Unable to aggregate responses. Please try again or contact support.',
    );
  }

  return modeResponse;
}

const normalizeParsedFieldValue = (
  parsedFieldValue: number | string | null | undefined,
) => {
  // This is to handle cases where the same value is represented in slightly different ways
  if (isNullOrUndefined(parsedFieldValue)) {
    return 'null';
  }
  if (typeof parsedFieldValue === 'string') {
    return parsedFieldValue;
  }
  if (parsedFieldValue > 1000) {
    return Math.round(parsedFieldValue / 2) * 2; // Bucketing values by rounding to the nearest 2
  }
  return parsedFieldValue;
};
